import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, StatusPill } from "@/components/PageChrome";
import { Input, Button, Select } from "@/components/controls";
import { Search, RefreshCw, TrendingUp, Save, Filter } from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import ImageThumb from "@/components/ImageThumb";

const STATUS_OPTIONS = ["Pending", "Updated"];

export default function PriceUpdate() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("");
  const [drafts, setDrafts] = useState({}); // {orderId: {updated_price, reseller_updated_price, status}}
  const { canEdit } = usePerms();
  const editable = canEdit("orders");

  const load = async () => {
    setBusy(true);
    try {
      const params = { limit: 500 };
      if (statusFilter) params.price_updated_status = statusFilter;
      if (marketplaceFilter) params.marketplace = marketplaceFilter;
      if (q) params.q = q;
      const { data } = await api.get("/price-update", { params });
      setRows(data || []);
      setDrafts({});
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { load(); }, [statusFilter, marketplaceFilter]); // eslint-disable-line

  const uniqueMarketplaces = useMemo(() => [...new Set(rows.map((r) => r.marketplace).filter(Boolean))], [rows]);

  const patchDraft = (id, key, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [key]: value } }));
  };

  const saveRow = async (row) => {
    const draft = drafts[row.id];
    if (!draft) return;
    try {
      const { data } = await api.patch(`/price-update/${row.id}`, draft);
      const fan = data?.fanout;
      if (fan && fan.created > 0) {
        toast.success(`Saved #${row.system_id} · +${fan.created} direct entr${fan.created === 1 ? "y" : "ies"} for mapped marketplaces`);
      } else {
        toast.success(`Saved #${row.system_id}`);
      }
      // Optimistic update
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, ...draft } : r));
      setDrafts((d) => {
        const c = { ...d }; delete c[row.id]; return c;
      });
      if (fan && fan.created > 0) {
        // Pull the new direct entries into the current view.
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveAll = async () => {
    const ids = Object.keys(drafts);
    if (!ids.length) { toast.error("No changes to save"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.patch(`/price-update/${id}`, drafts[id]);
        ok++;
      } catch {
        fail++;
      }
    }
    toast.success(`Saved ${ok}${fail ? `, failed ${fail}` : ""}`);
    setBusy(false);
    load();
  };

  const stats = useMemo(() => {
    const pending = rows.filter((r) => (r.price_updated_status || "Pending") === "Pending").length;
    const done = rows.length - pending;
    return { total: rows.length, pending, done };
  }, [rows]);

  return (
    <>
      <PageHeader title="Price Update" subtitle="Audit view of orders whose Updated Price or Reseller Updated Price has been set"
        testId={T.moduleTitle("price-update")} module="price-update">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="pu-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="SKU / MI / Order ID…"
            className="h-8 pl-7 pr-2 w-64 text-[13px] border border-slate-200 rounded-sm" />
        </div>
        <Select testId="pu-filter-status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} placeholder="All statuses" />
        <Select testId="pu-filter-marketplace" value={marketplaceFilter} onChange={setMarketplaceFilter} options={uniqueMarketplaces} placeholder="All marketplaces" />
        <Button onClick={load} testId="pu-refresh"><RefreshCw size={13} /> Refresh</Button>
        {editable && (
          <Button variant="primary" onClick={saveAll} disabled={!Object.keys(drafts).length || busy} testId="pu-save-all">
            <Save size={13} /> Save All ({Object.keys(drafts).length})
          </Button>
        )}
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Total Rows</div>
            <div className="font-mono text-xl font-semibold text-slate-800">{stats.total}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Pending</div>
            <div className="font-mono text-xl font-semibold text-amber-800">{stats.pending}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Updated</div>
            <div className="font-mono text-xl font-semibold text-emerald-800">{stats.done}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-260px)]">
            <table className="erp-table" data-testid="pu-table">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr>
                  <th>Sys ID</th>
                  <th>Image</th>
                  <th>System SKU</th>
                  <th>Marketplace ID</th>
                  <th>Marketplace</th>
                  <th className="text-right">Current SP ₹</th>
                  <th className="text-right">Suggestive SP ₹</th>
                  <th className="text-right">Updated Price ₹</th>
                  <th className="text-right">Reseller Updated ₹</th>
                  <th>Status</th>
                  <th>Last Updated By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const d = drafts[r.id] || {};
                  const upd = d.updated_price ?? r.updated_price ?? "";
                  const rup = d.reseller_updated_price ?? r.reseller_updated_price ?? "";
                  const st = d.price_updated_status ?? r.price_updated_status ?? "Pending";
                  const isDirty = drafts[r.id] !== undefined;
                  return (
                    <tr key={r.id} data-testid={`pu-row-${r.system_id}`} className={isDirty ? "bg-amber-50/40" : ""}>
                      <td>
                        <a href={`/orders?sys=${r.system_id}`} className="font-mono text-indigo-700 hover:underline">#{r.system_id}</a>
                      </td>
                      <td>
                        {r.image_url ? <ImageThumb src={r.image_url} size={64} testId={`price-thumb-${r.id}`} /> : "—"}
                      </td>
                      <td className="font-mono text-[11px]">{r.system_sku}</td>
                      <td className="font-mono text-[11px]">{r.marketplace_identifier || "—"}</td>
                      <td>{r.marketplace || "—"}</td>
                      <td className="font-mono text-right text-slate-600">{r.current_item_price != null ? Number(r.current_item_price).toFixed(2) : "—"}</td>
                      <td className="font-mono text-right font-semibold text-indigo-800">{r.suggestive_sp != null ? Number(r.suggestive_sp).toFixed(2) : "—"}</td>
                      <td>
                        {editable
                          ? <Input testId={`pu-price-${r.system_id}`} type="number" value={upd}
                                   onChange={(e) => patchDraft(r.id, "updated_price", e.target.value === "" ? "" : Number(e.target.value))}
                                   className="w-24 text-right font-mono" />
                          : <span className="font-mono">{upd || "—"}</span>}
                      </td>
                      <td>
                        {editable
                          ? <Input testId={`pu-reseller-${r.system_id}`} type="number" value={rup}
                                   onChange={(e) => patchDraft(r.id, "reseller_updated_price", e.target.value === "" ? "" : Number(e.target.value))}
                                   className="w-24 text-right font-mono" />
                          : <span className="font-mono">{rup || "—"}</span>}
                      </td>
                      <td>
                        {editable
                          ? <Select testId={`pu-status-${r.system_id}`} options={STATUS_OPTIONS} value={st}
                                    onChange={(v) => patchDraft(r.id, "price_updated_status", v)} allowEmpty={false} />
                          : <StatusPill value={st} tone={st === "Updated" ? "emerald" : "amber"} />}
                      </td>
                      <td className="text-[11px]" data-testid={`pu-updated-by-${r.system_id}`}>
                        {r.last_status_updated_by ? (
                          <div>
                            <div className="font-mono text-slate-700">{r.last_status_updated_by}</div>
                            {r.last_status_updated_at && (
                              <div className="text-[10px] text-slate-400">{new Date(r.last_status_updated_at).toLocaleString()}</div>
                            )}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td>
                        {editable && isDirty && (
                          <Button variant="primary" onClick={() => saveRow(r)} testId={`pu-save-${r.system_id}`}>
                            <Save size={11} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={12} className="text-center py-8 text-slate-500 border-t border-slate-100">
                    {busy ? "Loading…" : "No matching orders. Only orders whose Updated Price or Reseller Updated Price has a value appear here."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </PageBody>
    </>
  );
}
